import { assertUnreachable, } from '@lightdash/common';
const EMPTY = {
    nodes: [],
    links: [],
    maxDepth: 0,
    hasCycle: false,
};
const linkKey = (source, target) => `${source}→${target}`;
// One link per source→target pair, summing the metric across rows.
const aggregateLinks = (rows, { sourceFieldId, targetFieldId, metricFieldId }) => {
    const aggregated = new Map();
    for (const row of rows) {
        const sourceCell = row[sourceFieldId];
        const targetCell = row[targetFieldId];
        const metricCell = row[metricFieldId];
        if (!sourceCell || !targetCell || !metricCell)
            continue;
        const source = String(sourceCell.value.formatted);
        const target = String(targetCell.value.formatted);
        const value = Number(metricCell.value.raw);
        if (isNaN(value) || value <= 0)
            continue;
        const key = linkKey(source, target);
        const existing = aggregated.get(key);
        if (existing) {
            existing.value += value;
            existing.meta.rows.push(row);
        }
        else {
            aggregated.set(key, {
                source,
                target,
                value,
                meta: { value: metricCell.value, rows: [row] },
            });
        }
    }
    return aggregated;
};
// Kahn's algorithm: detects a cycle and returns the longest-path depth.
const analyzeFlowGraph = (aggregated) => {
    const nodes = new Set();
    const adjacency = new Map();
    const inDegree = new Map();
    for (const { source, target } of aggregated.values()) {
        nodes.add(source);
        nodes.add(target);
        if (!adjacency.has(source))
            adjacency.set(source, []);
        adjacency.get(source).push(target);
        inDegree.set(target, (inDegree.get(target) ?? 0) + 1);
        if (!inDegree.has(source))
            inDegree.set(source, 0);
    }
    const depthByNode = new Map();
    const queue = [];
    for (const node of nodes) {
        if ((inDegree.get(node) ?? 0) === 0) {
            queue.push(node);
            depthByNode.set(node, 0);
        }
    }
    let processed = 0;
    let maxDepth = 0;
    // Read through a head index; shift() is linear in the queue length
    for (let head = 0; head < queue.length; head += 1) {
        const node = queue[head];
        processed += 1;
        const depth = depthByNode.get(node) ?? 0;
        for (const target of adjacency.get(node) ?? []) {
            const nextDepth = Math.max(depthByNode.get(target) ?? 0, depth + 1);
            depthByNode.set(target, nextDepth);
            if (nextDepth > maxDepth)
                maxDepth = nextDepth;
            const remaining = (inDegree.get(target) ?? 0) - 1;
            inDegree.set(target, remaining);
            if (remaining === 0)
                queue.push(target);
        }
    }
    return { hasCycle: processed < nodes.size, maxDepth };
};
// One node per unique label: the aggregated links, unchanged. Acyclic only.
const buildMergedSankey = (aggregated, maxDepth) => {
    const names = new Set();
    const links = [];
    for (const link of aggregated.values()) {
        names.add(link.source);
        names.add(link.target);
        links.push({
            source: link.source,
            target: link.target,
            value: link.value,
            meta: link.meta,
        });
    }
    return {
        nodes: Array.from(names).map((name) => ({ name, label: name })),
        links,
        maxDepth,
    };
};
// Direct flows only: a strict two-column source→target view. Source and target
// identities are kept separate so ECharts never chains them into extra steps,
// which also means cyclic data renders fine (the bipartite output is acyclic).
const buildDirectSankey = (aggregated) => {
    const nodes = new Map(); // node id -> label
    const links = [];
    for (const link of aggregated.values()) {
        const sourceId = `source:${link.source}`;
        const targetId = `target:${link.target}`;
        nodes.set(sourceId, link.source);
        nodes.set(targetId, link.target);
        links.push({
            source: sourceId,
            target: targetId,
            value: link.value,
            meta: link.meta,
        });
    }
    return {
        nodes: Array.from(nodes, ([name, label]) => ({ name, label })),
        links,
        maxDepth: 1,
    };
};
// Unroll into depth layers via BFS so cyclic flows can render as a DAG: a label
// seen at multiple depths splits into "Label - Step N" nodes (the node id),
// while its label stays the base name.
const buildSteppedSankey = (aggregated) => {
    const outgoing = new Map();
    for (const link of aggregated.values()) {
        if (!outgoing.has(link.source))
            outgoing.set(link.source, new Set());
        outgoing.get(link.source).add(link.target);
    }
    const allTargets = new Set(Array.from(aggregated.values()).map((l) => l.target));
    const allSources = new Set(Array.from(aggregated.values()).map((l) => l.source));
    let roots = [...allSources].filter((s) => !allTargets.has(s));
    if (roots.length === 0)
        roots = [...allSources].slice(0, 1); // pure cycle: pick any source
    const MAX_DEPTH = 50;
    const nodeDepthMap = new Map();
    const edgeInstances = [];
    const placedOriginalEdges = new Set();
    const queue = roots.map((n) => ({ name: n, depth: 0 }));
    const visited = new Set(); // "name:depth"
    let maxDepth = 0;
    for (let head = 0; head < queue.length; head += 1) {
        const { name, depth } = queue[head];
        if (depth > MAX_DEPTH)
            continue;
        const key = `${name}:${depth}`;
        if (visited.has(key))
            continue;
        visited.add(key);
        if (!nodeDepthMap.has(name))
            nodeDepthMap.set(name, new Set());
        nodeDepthMap.get(name).add(depth);
        if (depth > maxDepth)
            maxDepth = depth;
        const targets = outgoing.get(name);
        if (!targets)
            continue;
        for (const target of targets) {
            const originalEdgeKey = linkKey(name, target);
            if (placedOriginalEdges.has(originalEdgeKey))
                continue;
            placedOriginalEdges.add(originalEdgeKey);
            const targetDepth = depth + 1;
            edgeInstances.push({
                source: name,
                sourceDepth: depth,
                target,
                targetDepth,
            });
            queue.push({ name: target, depth: targetDepth });
        }
    }
    const multiDepthNodes = new Set();
    for (const [name, depths] of nodeDepthMap) {
        if (depths.size > 1)
            multiDepthNodes.add(name);
    }
    const getNodeId = (name, depth) => multiDepthNodes.has(name) ? `${name} - Step ${depth}` : name;
    const nodes = new Map(); // node id -> label
    const links = [];
    const placedLinks = new Set();
    for (const edge of edgeInstances) {
        const sourceId = getNodeId(edge.source, edge.sourceDepth);
        const targetId = getNodeId(edge.target, edge.targetDepth);
        nodes.set(sourceId, edge.source);
        nodes.set(targetId, edge.target);
        const key = linkKey(sourceId, targetId);
        if (placedLinks.has(key))
            continue;
        placedLinks.add(key);
        const aggLink = aggregated.get(linkKey(edge.source, edge.target));
        if (!aggLink)
            continue;
        links.push({
            source: sourceId,
            target: targetId,
            value: aggLink.value,
            meta: aggLink.meta,
        });
    }
    return {
        nodes: Array.from(nodes, ([name, label]) => ({ name, label })),
        links,
        maxDepth,
    };
};
/**
 * Build Sankey nodes & links according to the chosen layout:
 * - `direct`: two-column source→target pairs (no chaining)
 * - `merged`: one node per label (acyclic only; falls back to multi-step)
 * - `multi-step`: depth-unrolled journeys with "Step N" instances
 */
export const transformSankeyData = (rows, fields, { nodeLayout }) => {
    const aggregated = aggregateLinks(rows, fields);
    if (aggregated.size === 0)
        return EMPTY;
    const { hasCycle, maxDepth } = analyzeFlowGraph(aggregated);
    let build;
    switch (nodeLayout) {
        case 'direct':
            build = buildDirectSankey(aggregated);
            break;
        case 'merged':
            // merging needs a DAG, so cyclic data falls back to multi-step
            build = hasCycle
                ? buildSteppedSankey(aggregated)
                : buildMergedSankey(aggregated, maxDepth);
            break;
        case 'multi-step':
            build = buildSteppedSankey(aggregated);
            break;
        default:
            return assertUnreachable(nodeLayout, `Unknown sankey node layout: ${nodeLayout}`);
    }
    return { ...build, hasCycle };
};
